//
// Copyright (c) 2011-2017 by Curalate, Inc.
//

import _ from "js/underscore-mixins";

(function() {
    /**
     * Generates the category used by GA tracking events
     */
  const trackingCategory = function(scope) {
    if (scope.trackingCategory) {
      return `${scope.trackingCategory}-${scope.name}`;
    }

    return scope.name;
  };

    /**
     * Creates a dynamic component which can be associated with one to many 'view's, the most common
     * being a table view and grid view.
     *
     * @attr name                   A unique name for the datatable. This is used for both internal and external purposes (GA events will include the
     *                              'name', for example)
     * @attr onRefreshData          Callback which is invoked when the datatable needs to be reload itself
     * @attr onError                Callback which is invoked when an error occurs while populating the datatable.
     * @attr emptyState             Shown when no results are returned
     * @attr responseFormatter      Callback which formats response data into the format expected by this component.
     * @attr withPagination         If enabled, this will include a pagination section below the datatable.
     * @attr withServerSideUpdates  If enabled, this will invoke the onRefreshData function (and ostensibly a network call) whenever the table is
     *                              paginated or re-sorted.
     * @attr defaultView            Defines the 'view' to be shown by default.
     * @attr defaultSort            Defines the default sort of the datatable.  Note that if server-side updates are enabled you should ensure this
     *                              value matches the default sort of the network call.
     * @attr defaultSortDirection   Direction of the default sort, either 'asc' or 'desc'.
     * @attr outerContext           Use this if you need to make functions available inside the context of the datatable
     * @attr trackingCategory       Tracking events are enabled by default in this component, however if you define trackingCategory then this value will
     *                              be prepended to the auto-generated event category.
     */
  angular.module('curalate').directive('datatable', [
    '$compile',
    '$interval',
    '$rootScope',
    'StringUtils',
    'TrackingService',
    function(
            $compile,
            $interval,
            $rootScope,
            StringUtils,
            TrackingService,
        ) {
      return {
        restrict: 'E',
        template: '<div class="datatable-next" style="display:none">' +
                               '<div ng-if="!scope.model.isLoadingOverlayDisabled" class="datatable-loading-container">' +
                                   '<div class="loading-overlay" ng-show="model.loading"></div>' +
                               '</div>' +
                               '<div ng-transclude></div>' +
                           '</div>',
        scope: {
          name: '@',
          onRefreshData: '&',
          onError: '&',
          emptyState: '@?',
          responseFormatter: '&?',
          withPagination: '@?',
          withServerSideUpdates: '@?',
          defaultView: '=',
          defaultSort: '=?',
          defaultSortDirection: '@?',
          outerContext: '=',
          trackingCategory: '@',
          itemsPerPage: '=',
        },
        transclude: true,
        controller: ['$scope', 'DatatableService', function($scope, DatatableService) {
                    // Private functions

          const updatePagination = function() {
            if ($scope.model.isServerSideEnabled) {
              $scope.model.items = $scope.model.savedItems; // The items returned by the server is what we are going to display
            } else {
              $scope.model.items = DatatableService.sortAndPaginate($scope.model.savedItems, $scope.model.pagination, $scope.model.sorting);
            }

            $scope.model.pagination.pages = DatatableService.generatePages($scope.model.pagination);
            $scope.model.pagination.maxPages = Math.ceil($scope.model.pagination.total / $scope.model.pagination.itemsPerPage);
            DatatableService.notifyPaginationUpdate($scope.name, $scope.model.pagination);
          };

          const updateState = function() {
            if ($scope.model.isServerSideEnabled) {
              $scope.model.reloadData();
            } else {
              updatePagination();
            }
          };

          const defaultItemsPerPage = (!!$scope.itemsPerPage && !isNaN(parseInt($scope.itemsPerPage))) ? parseInt($scope.itemsPerPage) : 10;

          $scope.model = {
            savedItems: [], // Internal items state - not exposed publicly
            items: [],
            pagination: {
              currentPage: 1,
              pages: [],
              itemsPerPage: defaultItemsPerPage,
              total: 0,
              start: 0,
              end: 0,
            },
            sorting: {
              currentSortKey: null,
              direction: 'desc',
            },
            currentFilterKey: null,
            loading: false,
            currentView: null,
            compiledTemplates: {}, // Contains the 'link' functions for all templates declared for this datatable
            isServerSideEnabled: false,
            isPaginationEnabled: false,
            responseFormatterEnabled: false, // If defined, the server-side response is sent through this function before being parsed
            multiCols: {}, // Keeps track of the last selected sortKey for multi data columns
          };

          $scope.model.reloadData = function() {
            const params = {
              offset: ($scope.model.pagination.currentPage - 1) * $scope.model.pagination.itemsPerPage,
              limit: $scope.model.pagination.itemsPerPage,
              sort: $scope.model.sorting.currentSortKey,
              direction: $scope.model.sorting.direction,
              filter: $scope.model.currentFilterKey,
            };
            const refreshData = $scope.onRefreshData({ params });
            let theItems = [];

            if (DatatableService.isDeferredObject(refreshData)) {
              $scope.model.loading = true;

              refreshData.then((data) => {
                if ($scope.model.isResponseFormatterEnabled) {
                  data = $scope.responseFormatter({ data });
                }

                if (data.items === undefined) {
                  throw new Error('Datatable response must contain an "items" field. ' +
                                        'Consider using the "response-formatter" attribute in your template.');
                }

                theItems = data.items;

                $scope.model.savedItems = theItems;
                $scope.model.pagination.total = data.totalCount || data.items.length;

                if ($scope.model.pagination.total === 0) {
                  DatatableService.changeView($scope.name, 'empty');
                } else {
                  DatatableService.changeView($scope.name, $scope.model.currentView);
                  updatePagination();
                }
              }).catch((err) => {
                $scope.onError(err); // Notify caller that an error occurred when loading the datatable
              }).finally(() => {
                $scope.model.loading = false;
              });
            } else {
              if (typeof refreshData === 'function') {
                theItems = refreshData();
              } else {
                theItems = refreshData;
              }

              $scope.model.savedItems = theItems;
              $scope.model.pagination.total = theItems.length;

              if ($scope.model.pagination.total === 0 && $scope.emptyState) {
                DatatableService.changeView($scope.name, 'empty');
              } else {
                DatatableService.changeView($scope.name, $scope.model.currentView);
                updatePagination();
              }
            }
          };

          $scope.model.sort = function(sortKey) {
                        // This allows the table to sort on the correct selected key when a multi column header is clicked on outside of the text itself.
            if (sortKey in $scope.model.multiCols) {
              sortKey = $scope.model.multiCols[sortKey];
            }
            if (!$scope.model.loading) {
              if (sortKey === $scope.model.sorting.currentSortKey) {
                                // user clicked on active sort option - flip bit on sort order
                if ($scope.model.sorting.direction === 'asc') {
                  $scope.model.sorting.direction = 'desc';
                } else {
                  $scope.model.sorting.direction = 'asc';
                }

                TrackingService.recordEvent(trackingCategory($scope), 'DatatableChangeSortDirection', sortKey);
              } else {
                                // user clicked on new sort option
                $scope.model.sorting.currentSortKey = sortKey;

                TrackingService.recordEvent(trackingCategory($scope), 'DatatableChangeSort', sortKey);
              }

              $scope.model.pagination.currentPage = 1;

              updateState();
            }
          };

          $scope.model.changeSortDirection = function() {
            if ($scope.model.sorting.direction === 'asc') {
              $scope.model.sorting.direction = 'desc';
            } else {
              $scope.model.sorting.direction = 'asc';
            }

            $scope.model.pagination.currentPage = 1;

            updateState();

            TrackingService.recordEvent(trackingCategory($scope), 'DatatableChangeSortDirection', $scope.model.sorting.currentSortKey);
          };

          $scope.model.filter = function(filterKey) {
            if (filterKey !== $scope.model.currentFilterKey) {
              $scope.model.currentFilterKey = filterKey;
              $scope.model.pagination.currentPage = 1;
              updateState();

              TrackingService.recordEvent(trackingCategory($scope), 'DatatableFilter', filterKey);
            }
          };

          const unbindReloadDatatables = $scope.$on('datatable:*:reloadDatatables', () => {
            $scope.model.reloadData();
          });

          const unbindReloadDatatable = $scope.$on('datatable:reloadDatatable', ($event, tableName) => {
            if (tableName === $scope.name) {
              $scope.model.reloadData();
            }
          });

          $scope.$on('$destroy', unbindReloadDatatables);
          $scope.$on('$destroy', unbindReloadDatatable);

                    //
          $scope.$on(StringUtils.formatString('datatable:%s:reset-multi-cols', $scope.name), () => {
            $scope.model.multiCols = {};
          });

                    // Sets a new sort key for the table. If changeDirection is false, the table won't change the
                    // sort direction if the active sort key for the table is already the specified sort key.
          $scope.$on(StringUtils.formatString('datatable:%s:new-sort', $scope.name), ($event, sortKey, changeDirection) => {
            if (changeDirection || (sortKey !== $scope.model.sorting.currentSortKey)) {
              $scope.model.sort(sortKey);
            } else {
              $scope.model.reloadData();
            }
          });

                    // Template functions

          this.registerView = function(name, template) {
            if ($scope.model.compiledTemplates.hasOwnProperty(name)) {
              throw new Error(`Template with name "${name}" has already been defined`);
            }

            $scope.model.compiledTemplates[name] = $compile(template);
          };

          this.registerHeaderView = function(template) {
            $scope.model.headerTemplate = $compile(template);
          };

                    // Pagination

          this.updatePage = function(pageNumber) {
            pageNumber = parseInt(pageNumber, 10);

            if (isNaN(pageNumber)) {
              return; // not a valid number
            }

            const maxPages = Math.ceil($scope.model.pagination.total / $scope.model.pagination.itemsPerPage);
            const newPage = Math.max(Math.min(pageNumber, maxPages), 1);

            $scope.model.pagination.currentPage = newPage;
            $scope.model.pagination.pages = DatatableService.generatePages($scope.model.pagination);
            $scope.model.pagination.start = (newPage - 1) * $scope.model.pagination.itemsPerPage;
            $scope.model.pagination.end = Math.min(newPage * $scope.model.pagination.itemsPerPage, maxPages);
            $scope.model.pagination.maxPages = maxPages;

            TrackingService.recordEvent(trackingCategory($scope), 'DatatablePagination', `PageNumber:${pageNumber}`);

            updateState();
          };

          this.goToFirstPage = function() {
            $scope.model.pagination.currentPage = 1;

            TrackingService.recordEvent(trackingCategory($scope), 'DatatablePagination', 'FirstPage');

            updateState();
          };

          this.goToPreviousPage = function() {
            $scope.model.pagination.currentPage = Math.max(1, $scope.model.pagination.currentPage - 1);

            TrackingService.recordEvent(trackingCategory($scope), 'DatatablePagination', 'PrevPage');

            updateState();
          };

          this.goToNextPage = function() {
            const maxPages = Math.ceil($scope.model.pagination.total / $scope.model.pagination.itemsPerPage);

            $scope.model.pagination.currentPage = Math.min($scope.model.pagination.currentPage + 1, maxPages);

            TrackingService.recordEvent(trackingCategory($scope), 'DatatablePagination', 'NextPage');

            updateState();
          };

          this.goToLastPage = function() {
            $scope.model.pagination.currentPage = Math.ceil($scope.model.pagination.total / $scope.model.pagination.itemsPerPage);

            TrackingService.recordEvent(trackingCategory($scope), 'DatatablePagination', 'LastPage');

            updateState();
          };

          this.changeNumPerPage = function(itemsPerPage) {
            const maxPages = Math.ceil($scope.model.pagination.total / itemsPerPage);
            $scope.model.pagination.itemsPerPage = itemsPerPage;
            if ($scope.model.pagination.currentPage > maxPages) {
              $scope.model.pagination.currentPage = maxPages;
            }

            TrackingService.recordEvent(trackingCategory($scope), 'DatatableChangePerPage', itemsPerPage);

            updateState();
          };

                    /**
                     * Returns the different 'items per page' options available to the user.
                     */
          this.getPageOptions = function() {
            return (defaultItemsPerPage === 10) ? [10, 25, 50, 100] : [1, 2, 3, 4].map(n => n * defaultItemsPerPage);
          };

          this.getTotalItems = function() {
            return $scope.model.pagination.total;
          };

          this.isLoading = function() {
            return $scope.model.loading;
          };

          this.hasCustomHeader = function() {
            return $scope.model.hasCustomHeader;
          };

          this.isLoadingOverlayDisabled = function() {
            return $scope.model.isLoadingOverlayDisabled;
          };
        }],
        link(scope, element, attributes, controller) {
          const contentContainer = angular.element('<div class="datatable-content-container clearfix"></div>');
          const paginationContainer = angular.element('<div class="datatable-pagination-container"></div>');
          let defaultView;
          let compiledAndLinkedCustomHeader;

          if (!attributes.name) {
            throw new Error('Datatable must define a name attribute');
          }

          if (!attributes.onRefreshData) {
            throw new Error('on-refresh-data function must be defined');
          }

          if (_.isEmpty(scope.model.compiledTemplates)) {
            throw new Error('No templates have been defined for this datatable!');
          }

          if (scope.model.headerTemplate) {
            compiledAndLinkedCustomHeader = scope.model.headerTemplate(scope.$new(), angular.noop);
          }

                    // Select default view - it can either be defined explicitly or chosen implicitly so long as only
                    // one template is defined.
          const viewsExcludingEmpty = _(scope.model.compiledTemplates).omit('empty');
          if (_.keys(viewsExcludingEmpty).length > 1 && !viewsExcludingEmpty[scope.defaultView]) {
            throw new Error('A default-view must be defined if more than one template is associated with this datatable');
          } else if (scope.defaultView) {
            defaultView = scope.defaultView;
          } else {
            defaultView = _.keys(viewsExcludingEmpty)[0];
          }

          scope.model.currentView = defaultView;

                    // Select default sorting - this is optional
          if (attributes.hasOwnProperty('defaultSort')) {
            scope.model.sorting.currentSortKey = attributes.defaultSort;
          }

                    // Set default sort direction
          if (attributes.hasOwnProperty('defaultSortDirection')) {
            scope.model.sorting.direction = attributes.defaultSortDirection;
          }

          scope.model.isServerSideEnabled = attributes.hasOwnProperty('withServerSideUpdates');
          scope.model.isPaginationEnabled = attributes.hasOwnProperty('withPagination');
          scope.model.isLoadingOverlayDisabled = attributes.hasOwnProperty('disableLoadingOverlay');
          scope.model.isResponseFormatterEnabled = attributes.hasOwnProperty('responseFormatter');

          element
                        .find('.datatable-next')
                        .empty() // Clear out the templates we used for bootstrapping purposes
                        .show()
                        .append(contentContainer)
                        .append('<div class="clearfix"></div>')
                        .append(paginationContainer);

          if (compiledAndLinkedCustomHeader) {
            element
                            .find('.datatable-next')
                            .prepend(compiledAndLinkedCustomHeader);
          }

          scope.model.hasCustomHeader = !!compiledAndLinkedCustomHeader;

          element
                        .find('.datatable-content-container')
                        .append(scope.model.compiledTemplates[defaultView](scope, angular.noop));

          if (scope.model.isPaginationEnabled) {
            element
                            .find('.datatable-pagination-container')
                            .append(`<datatable-pagination name="${scope.name}"></datatable-pagination>`);

            const pagination = element
                            .find('.datatable-pagination-container')
                            .find('datatable-pagination');
            $compile(pagination)(scope);
          }

                    // Create Empty View
          if (attributes.hasOwnProperty('emptyState')) {
            const emptyWrapper = angular.element('<div />').addClass('col-xs-12 datatable-empty-state').html(attributes.emptyState);
            controller.registerView('empty', emptyWrapper);
          }

          $rootScope.$on(StringUtils.formatString('datatable:%s:change-view', scope.name), (thing, view) => {
            const currentView = scope.model.currentView;
                        // Here be dragons...
                        // Clear out watchers in this scope.  Without this, every view change retains the old watchers, which makes
                        // the datatable progressively slower...

                        // Empty is a "magic" view that should not be saved as the currentView
                        // This is so we can go "back" to the currently selected view, once we are no longer empty
            if (view === 'empty') {
              scope.model.items = [];
              scope.model.savedItems = [];
            } else {
              scope.model.currentView = view;
            }

            scope.$$watchers = [];

                        /**
                         *  Empty the container in the following cases:
                         *      1) Moving into the 'empty' view
                         *      2) There is data already present (savedItems.length or pagination.total), in which case the new view can be populated
                         *      3) There is data loading (in which case there is a handler to check if a transition back to 'empty' is needed)
                         */
            if (view === 'empty' || scope.model.savedItems.length || scope.model.pagination.total || scope.model.loading) {
              element
                                .find('.datatable-content-container')
                                .empty()
                                .append(scope.model.compiledTemplates[view](scope, angular.noop));
            }

            if (view !== currentView && view !== 'empty') {
              TrackingService.recordEvent(trackingCategory(scope), 'DatatableChangeView', view);
            }
          });

                    // Initialize the datatable
          const loader = $interval(
                        () => {
                          if (!scope.model.isPaginationEnabled || element.find('.datatable-next-pagination').length > 0) {
                            $interval.cancel(loader);
                            scope.model.reloadData();
                          }
                        },
                        25, // check every 25 ms
                        120, // stop checking after 3 seconds
                    );
        },
      };
    }]);
})();
